import React, { useEffect, useRef, useState } from "react";
import { CalendarDays, X } from "lucide-react";

const DAYS = ["D", "L", "M", "M", "J", "V", "S"];

const formatDisplayDate = (date) => {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
};

const ExpenseDatePicker = ({
  value,
  onChange,
  period,
  excludedDates = [],
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const closeCalendar = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeWithEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", closeCalendar);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("mousedown", closeCalendar);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, []);

  const [year, month] = period
    ? period.split("-").map(Number)
    : [null, null];
  const daysInMonth = year && month ? new Date(year, month, 0).getDate() : 0;
  const firstDay = year && month ? new Date(year, month - 1, 1).getDay() : 0;
  const monthLabel = year && month
    ? new Intl.DateTimeFormat("fr-CA", {
        month: "long",
        year: "numeric",
      }).format(new Date(year, month - 1, 1))
    : "Choisissez d'abord une période";

  const calendarCells = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];

  const formatDate = (day) =>
    `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  return (
    <div ref={containerRef} className="relative mt-1 w-full">
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && period && setIsOpen((open) => !open)}
          disabled={disabled || !period}
          className="flex w-full items-center rounded-xl border border-gray-200 bg-white p-3 pr-20 text-left shadow-sm transition-all duration-200 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:cursor-not-allowed disabled:bg-gray-100"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <span className={value ? "text-gray-900" : "text-gray-400"}>
            {formatDisplayDate(value) || "JJ/MM/AAAA"}
          </span>
        </button>

        {value && !disabled && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-10 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Effacer la date"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <CalendarDays className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
      </div>

      {isOpen && (
        <div
          className="absolute left-0 z-50 mt-2 w-72 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl"
          role="dialog"
          aria-label="Choisir une date de dépense"
        >
          <p className="mb-3 text-center font-semibold capitalize text-blue-900">
            {monthLabel}
          </p>
          <div className="grid grid-cols-7 gap-1">
            {DAYS.map((day, index) => (
              <span
                key={`${day}-${index}`}
                className="py-1 text-center text-xs font-semibold text-gray-500"
              >
                {day}
              </span>
            ))}
            {calendarCells.map((day, index) => {
              if (!day) return <span key={`empty-${index}`} />;

              const date = formatDate(day);
              const isExcluded = excludedDates.includes(date);
              const isSelected = value === date;

              return (
                <button
                  key={date}
                  type="button"
                  disabled={isExcluded}
                  title={isExcluded ? "Date déjà utilisée" : undefined}
                  onClick={() => {
                    onChange(date);
                    setIsOpen(false);
                  }}
                  className={`h-9 rounded-lg text-sm transition ${
                    isExcluded
                      ? "cursor-not-allowed bg-gray-100 text-gray-300 line-through"
                      : isSelected
                        ? "bg-purple-600 font-semibold text-white"
                        : "text-gray-700 hover:bg-purple-100 hover:text-purple-800"
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-center text-xs text-gray-500">
            Les dates grisées sont déjà utilisées.
          </p>
        </div>
      )}
    </div>
  );
};

export default ExpenseDatePicker;
